import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { InlineNotice, type Notice } from "@/shared/organizations/InlineNotice";
import { Badge } from "@/shared/components/ui/badge";
import { SafeText } from "@/shared/components/SafeText";
import { TestPartitionBadge } from "@/shared/components/TestPartitionBadge";
import { formatDateShort } from "@/shared/lib/format-date";
import type { AdminOrganization, AdminOrganizationCounts } from "./types";
import { RenameOrganizationDialog } from "./RenameOrganizationDialog";
import { userPath } from "@/shared/users/user-link";

interface OrganizationHeaderProps {
  readonly organization: AdminOrganization;
  /** Whether the viewer may rename the organization (`org.name.edit`). */
  readonly canRename?: boolean;
}

const COUNT_KEYS = ["activeMembers", "inactiveMembers", "pendingInvitations", "locations"] as const;

function countLabelKey(key: keyof AdminOrganizationCounts, kind: AdminOrganization["kind"]) {
  // `locations` counts venues or properties depending on the organization.
  return key === "locations"
    ? `organizations.detail.counts.locations_${kind}`
    : `organizations.detail.counts.${key}`;
}

/**
 * Identity of the organization: name, kind, partition, deactivation, owner (by
 * display name only: the API sends no email) and the counts the API computed.
 */
export function OrganizationHeader({ organization, canRename = false }: OrganizationHeaderProps) {
  const { t, i18n } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const { owner, counts } = organization;
  const deactivatedOn = formatDateShort(organization.deactivatedAt, i18n.language);

  return (
    <header className="space-y-3">
      <p className="brand-eyebrow">{t("organizations.eyebrow")}</p>
      <div className="flex flex-wrap items-center gap-3">
        <h1 data-testid="organization-name" className="break-words text-4xl">
          <SafeText fallback={t("organizations.unnamed")}>{organization.name}</SafeText>
        </h1>
        {canRename && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="organization-rename"
            onClick={() => {
              setNotice(null);
              setRenaming(true);
            }}
          >
            <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t("organizationAccess.rename.action")}
          </Button>
        )}
        <Badge variant="outline" data-testid="organization-kind">
          {t(`organizations.kind.${organization.kind}`)}
        </Badge>
        <TestPartitionBadge isTest={organization.isTest} testId="organization-test-badge" />
        {!organization.active && (
          <Badge variant="secondary" data-testid="organization-inactive">
            {deactivatedOn
              ? t("organizations.deactivatedOn", { date: deactivatedOn })
              : t("organizations.inactive")}
          </Badge>
        )}
      </div>

      {notice && <InlineNotice notice={notice} testId="organization-notice" />}
      {canRename && (
        <RenameOrganizationDialog
          open={renaming}
          onOpenChange={setRenaming}
          organizationId={organization.id}
          currentName={organization.name}
          nameIsDerived={organization.nameIsDerived}
          onDone={() => {
            setNotice({ tone: "success", message: t("organizationAccess.rename.success") });
            setRenaming(false);
          }}
        />
      )}

      <dl className="grid gap-x-8 gap-y-1 font-sans text-sm sm:grid-cols-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <dt className="text-muted-foreground">{t("organizations.detail.owner")}</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <Link
              to={userPath(owner.id)}
              data-testid="organization-owner"
              className="break-words font-medium text-primary underline-offset-2 hover:underline"
            >
              <SafeText fallback={t("organizations.unnamedUser")}>{owner.displayName}</SafeText>
            </Link>
            {!owner.isActive && (
              <Badge variant="secondary" data-testid="organization-owner-inactive">
                {t("organizations.detail.ownerInactive")}
              </Badge>
            )}
          </dd>
        </div>
        <div className="flex flex-wrap items-baseline gap-2">
          <dt className="text-muted-foreground">{t("organizations.detail.created")}</dt>
          <dd>{formatDateShort(organization.createdAt, i18n.language) || "—"}</dd>
        </div>
        <div className="flex flex-wrap items-baseline gap-2 break-all">
          <dt className="text-muted-foreground">{t("organizations.idLabel")}</dt>
          <dd data-testid="organization-id" className="font-mono">
            {organization.id}
          </dd>
        </div>
      </dl>

      <ul className="flex flex-wrap gap-3" data-testid="organization-counts">
        {COUNT_KEYS.map((key) => (
          <li
            key={key}
            data-testid={`organization-count-${key}`}
            className="rounded-md border border-border bg-card px-3 py-2 font-sans text-sm"
          >
            <span className="block text-xs uppercase tracking-wide text-muted-foreground">
              {t(countLabelKey(key, organization.kind))}
            </span>
            <span className="text-lg font-medium tabular-nums">{counts[key]}</span>
          </li>
        ))}
      </ul>
    </header>
  );
}
